import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  FC,
  MouseEvent,
  KeyboardEvent,
  FocusEvent,
  forwardRef,
  RefAttributes,
  SyntheticEvent
} from 'react';
import { DatePickerProps, DATE_PART } from './DatePickerTypes';
import { useOnClickOutside } from '../util';
import MonthToggle from './MonthToggle';
import {
  genDaysOfMonth,
  shiftDays,
  shiftDate,
  dateToObj,
  getMonths,
  getWeekdays,
  getWeeks,
  buildHandler,
  isEqual,
  blurCallback,
  checkOrDefault,
  pickerClass
} from './utils';
import { Entry } from '../Entry';
import { Error } from '../Error';
import { PickerElement } from './PickerElement';
import { time } from 'uniqid';
import { DATE_PICKER_DAYS_IN_WEEK } from '../constants';
import { get, isEmpty, trim } from 'lodash';
import { dayjs } from '@trip-ux/common';
import { Tooltip } from '../Tooltip';

const DatePickerInput: FC<
  {
    name: string;
    value: string;
    disabled: boolean;
    readOnly: boolean;
    id: string;
    onBlurHandler: (e: SyntheticEvent) => void;
    onChange: (e: SyntheticEvent) => void;
    onFocus: (e: SyntheticEvent) => void;
    onKeyDown: (e: SyntheticEvent) => void;
    onClick: (e: SyntheticEvent) => void;
    dateFormat: string;
    required: boolean;
    isPickerOpen: boolean;
    labels: {
      nextMonth?: string;
      prevMonth?: string;
      togglePicker?: string;
      textBoxAriaLabel?: string;
      toggleAriaLabel?: string;
      calendarAriaLabel?: string;
      placeholder?: string;
    };
    hasErrors: boolean;
    tooltipContent?: string;
  } & RefAttributes<HTMLInputElement>
> = forwardRef(
  (
    {
      name,
      value,
      disabled,
      readOnly,
      onBlurHandler,
      onChange,
      onFocus,
      dateFormat,
      required,
      onKeyDown,
      labels,
      onClick,
      id,
      isPickerOpen,
      hasErrors
    },
    ref
  ) => {
    const toggleButtonTitle = useMemo(() => get(labels, 'togglePicker'), [labels]);

    return (
      <div className="position-relative date-picker-input">
        <Entry
          id={id}
          data-testid={id}
          type="text"
          name={name}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          onBlur={onBlurHandler}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          mask={dateFormat.replace(/[dmy]{1}/gi, '9')}
          aria-required={required}
          autoComplete="off"
          placeholder={get(labels, 'placeholder')}
          ref={ref}
          aria-invalid={hasErrors}
          aria-describedby={`${id}-described`}
          className={trim(`${hasErrors && !disabled ? 'error' : ''} ${isPickerOpen ? 'active' : ''}`)}
        />
        <button
          type="button"
          onClick={onClick}
          aria-controls={`${id}-picker`}
          aria-expanded={isPickerOpen}
          title={toggleButtonTitle}
          data-testid={`${id}-toggle`}
          disabled={disabled || readOnly}
          aria-label={get(labels, 'toggleAriaLabel')}
          className={hasErrors && !disabled ? 'error-date-toggle' : ''}>
          <span aria-hidden="true" className={pickerClass(isPickerOpen)} />
        </button>
      </div>
    );
  }
);
DatePickerInput.displayName = 'DatePickerInput';

const DatePickerLabel = ({ showLabel, labelChildren, id, tooltipContent, name, className }) => {
  return (
    <div className={className}>
      <label htmlFor={id} className={!showLabel ? 'sr-only' : ''}>
        {labelChildren}
      </label>
      <div>
        {!isEmpty(tooltipContent) && (
          <Tooltip
            id={`${name}-tooltip-${id}`}
            ariaLabel={`${name}-tooltip`}
            bubbleWidth={200}
            title={tooltipContent}
            hover={false}
            hasCloseButton={true}
            closeButtonAria={`close-${name}-tooltip`}
          />
        )}
      </div>
    </div>
  );
};

const MonthToggler = ({ labels, disabledDays, months, focusedDate, shift, language, month, year }) => {
  return (
    <div className="date-picker-drop-header">
      <MonthToggle
        labels={labels}
        inc={-1}
        disabledDays={disabledDays}
        months={months}
        focusedDate={focusedDate}
        shift={shift}
        lang={language}
        id={'previous-month'}
      />
      <div>{`${months[month]} ${year}`}</div>
      <MonthToggle
        labels={labels}
        inc={1}
        disabledDays={disabledDays}
        months={months}
        focusedDate={focusedDate}
        shift={shift}
        lang={language}
        id={'next-month'}
      />
    </div>
  );
};

const updateValueFromParent = (inputValue, value, selectDate) => {
  if (inputValue !== value) {
    const currentVal = dayjs(inputValue);
    const currentYear = currentVal.year();
    const currentMonth = currentVal.month();
    const currentDate = currentVal.date();
    selectDate(null, currentYear, currentMonth, currentDate);
  }
};

export const DatePicker: FC<DatePickerProps & RefAttributes<HTMLInputElement>> = forwardRef(
  (
    {
      id = time('date-picker-'),
      language,
      dateFormat,
      defaultValue,
      defaultFocus,
      disabled = false,
      disabledDays = {},
      readOnly = false,
      onError = () => false,
      errors,
      onChange,
      required,
      labels,
      className = '',
      name,
      onBlur = () => true,
      onFocus = () => true,
      showLabel = true,
      children,
      tooltipContent,
      value: inputValue,
      isSearch
    },
    ref
  ) => {
    const { before, after } = disabledDays;
    const [isPickerOpen, setPickerOpen] = useState(false);
    const [isMonthToggle, setMonthToggle] = useState(false);
    const outsideClickTarget = useRef();
    useOnClickOutside(outsideClickTarget, () => setTimeout(() => isPickerOpen && setPickerOpen(false), 0));
    useEffect(() => {
      if (!isPickerOpen) {
        setMonthToggle(false);
      }
    }, [isPickerOpen]);

    const [selectedDate, setSelectedDate] = useState(checkOrDefault(defaultValue, dateFormat));
    const [focusedDate, setFocusedDate] = useState(
      defaultFocus ? dateToObj(defaultFocus) : selectedDate ? selectedDate : dateToObj(new Date())
    );
    const { year, month, date } = focusedDate;
    const [value, setValue] = useState<string>(defaultValue);
    const daysOfMonth = genDaysOfMonth(year, month, disabledDays);
    const weeks = getWeeks(daysOfMonth.length);
    const daysOfWeek = useMemo(() => getWeekdays(language), [language]);
    const months = useMemo(() => getMonths(language), [language]);
    useEffect(() => {
      if (defaultFocus) {
        setFocusedDate(dateToObj(defaultFocus));
      }
    }, [defaultFocus]);

    const shift = useCallback(
      (offset: number, type = DATE_PART.DAY, isFirstOfMonth = false, monthToggle = false) => {
        const requestedDate = shiftDate(focusedDate, offset, type, isFirstOfMonth);
        setMonthToggle(monthToggle);
        shiftDays(requestedDate, before, after, setFocusedDate);
      },
      [focusedDate, before, after]
    );

    const selectDate = useCallback(
      (_: MouseEvent | KeyboardEvent | FocusEvent, inYear: number, inMonth: number, inDate: number) => {
        const day = dayjs([inYear, inMonth, inDate]);
        const formattedDate = day.format(dateFormat);
        const oldDate = selectedDate ? dayjs(selectedDate as unknown as dayjs.ConfigType).format(dateFormat) : '';
        if (oldDate !== formattedDate || oldDate !== value) {
          setFocusedDate({
            year: inYear,
            month: inMonth,
            date: inDate
          });
          setSelectedDate({ year: inYear, month: inMonth, date: inDate });
          setValue(formattedDate);
          onChange(formattedDate);
        }
        setPickerOpen(false);
      },
      [dateFormat, onChange, selectedDate, value]
    );

    const deselectDate = useCallback(
      _ => {
        setValue('');
        setFocusedDate(dateToObj(new Date()));
        setSelectedDate(null);
        onChange('');
      },
      [onChange]
    );

    const onBlurHandler = useCallback(
      event => {
        onChange(event.currentTarget.value);
        blurCallback({
          event,
          defaultValue,
          dateFormat,
          required,
          onError,
          before,
          after,
          selectDate,
          onBlur,
          deselectDate
        });
      },
      [defaultValue, dateFormat, required, onError, before, after, selectDate, onBlur, deselectDate]
    );

    const arrowKeyHandler = useCallback(
      (event: KeyboardEvent) => buildHandler(event, shift, e => selectDate(e, year, month, date)),
      [year, month, date, selectDate, shift]
    );

    useEffect(() => {
      if (!isSearch) {
        updateValueFromParent(inputValue, value, selectDate);
      }
    }, [inputValue, isSearch]);

    return (
      <>
        <DatePickerLabel
          showLabel={showLabel}
          labelChildren={children}
          id={id}
          tooltipContent={tooltipContent}
          name={name}
          className={'date-picker-label'}
        />

        <div className={`date-picker position-relative ${className}`}>
          <DatePickerInput
            name={name}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            onBlurHandler={onBlurHandler}
            onChange={e => {
              onError();
              setValue(get(e.target, 'value'));
            }}
            ref={ref}
            onFocus={onFocus}
            dateFormat={dateFormat}
            required={required}
            onKeyDown={e => get(e, 'key') === 'Enter' && setPickerOpen(!isPickerOpen)}
            labels={labels}
            onClick={e => {
              e.preventDefault();
              setPickerOpen(!isPickerOpen);
            }}
            id={id}
            isPickerOpen={isPickerOpen}
            hasErrors={!isEmpty(errors)}
          />
          {isPickerOpen && !disabled && !readOnly && (
            <div
              id={`${id}-picker`}
              data-testid={`${id}-picker`}
              ref={outsideClickTarget}
              className="date-picker-drop position-absolute">
              <MonthToggler
                labels={labels}
                disabledDays={disabledDays}
                months={months}
                focusedDate={focusedDate}
                shift={shift}
                language={language}
                month={month}
                year={year}
              />
              <table
                className="date-picker-drop-grid"
                tabIndex={0}
                role="grid"
                aria-activedescendant={`cell-${year}-${month}-${date}`}
                onKeyDown={arrowKeyHandler}
                aria-label={labels.calendarAriaLabel}>
                <thead>
                  <tr className="date-picker-grid-row">
                    {daysOfWeek.map(day => (
                      <th key={day.title} role="columnheader" aria-label={day.title}>
                        <abbr title={day.title}>{day.abbr}</abbr>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weeks.map(week => (
                    <tr key={`${week}`} className="date-picker-grid-row">
                      {daysOfMonth
                        .slice(week * DATE_PICKER_DAYS_IN_WEEK, (week + 1) * DATE_PICKER_DAYS_IN_WEEK)
                        .map(day => (
                          <PickerElement
                            lang={language}
                            key={`${day.year}-${day.month}-${day.date}`}
                            day={day}
                            isFocused={isEqual(day, focusedDate)}
                            isMonthToggle={isMonthToggle}
                            selectedDate={selectedDate}
                            isHidden={day.month !== month}
                            selectDate={selectDate}
                          />
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Error errors={errors} callout={false} id={`${id}-described`} />
        </div>
      </>
    );
  }
);

DatePicker.displayName = 'DatePicker';
export default DatePicker;
